import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  forwardRef
} from "@angular/core";
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  NG_VALUE_ACCESSOR
} from "@angular/forms";
import { isEqual } from "lodash";
import {
  debounceTime,
  filter,
  first,
  map,
  pairwise,
  startWith,
  takeUntil
} from "rxjs/operators";

@Component({
  selector: "ta-json-text-editor",
  templateUrl: "./json-text-editor.template.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => JsonTextEditorComponent),
      multi: true
    }
  ]
})
export class JsonTextEditorComponent
  implements ControlValueAccessor, OnInit, OnDestroy {
  @Input()
  rootName = "root";

  get textControl(): AbstractControl {
    return this.formGroup.get("jsonText")!;
  }

  private textControlActionableChanges$;

  jsonIsValid = true;

  // value van be given as string or object/array, but it will be converted to object/string
  get value(): any {
    return JSON.parse(this.textControl.value);
  }
  set value(newVal: any) {
    if (typeof newVal === "string") {
      this.setFromString(newVal);
    } else if (typeof newVal === "number") {
      this.setFromNumber(newVal);
    } else if (typeof newVal === "boolean") {
      this.setFromBoolean(newVal);
    } else if (Array.isArray(newVal) || typeof newVal === "object") {
      this.setFromJson(newVal);
    } else {
      return;
    }
  }

  formGroup = this.fb.group({
    jsonText: this.fb.control("")
  });

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.initTextChangeSubscription();
  }

  private initTextChangeSubscription(): void {
    this.textControlActionableChanges$ = this.textControl.valueChanges.pipe(
      debounceTime(300),
      startWith(this.textControl.value),
      pairwise(),
      filter(([prev, curr]) => {
        try {
          return !isEqual(JSON.parse(prev), JSON.parse(curr));
        } catch {
          return true; // we'll catch the bad json string in setFromString
        }
      }),
      map(([_, curr]) => curr)
    );

    this.textControlActionableChanges$
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe((text) => {
        this.setFromString(text);
      });
  }

  private setFromString(newVal: string): void {
    try {
      JSON.parse(newVal); // throw error if not valid json
      this.setFormValue(newVal);
      this.jsonIsValid = true;
    } catch (e) {
      this.jsonIsValid = false;
    }
  }

  private setFromNumber(newVal: number): void {
    try {
      this.setFormValue(JSON.stringify(newVal));
      this.jsonIsValid = true;
    } catch (e) {
      this.jsonIsValid = false;
    }
  }

  private setFromBoolean(newVal: boolean): void {
    try {
      this.setFormValue(JSON.stringify(newVal));
      this.jsonIsValid = true;
    } catch (e) {
      this.jsonIsValid = false;
    }
  }

  private setFromJson(newVal: Record<string, any> | any[]): void {
    try {
      this.setFormValue(JSON.stringify(newVal, null, 2));
      this.jsonIsValid = true;
    } catch {
      this.jsonIsValid = false;
    }
  }

  private setFormValue(jsonText: string): void {
    this.textControl.patchValue(jsonText);
  }

  writeValue(val: any): void {
    this.value = val;
  }

  registerOnChange(propagateChanges: (...args: any[]) => void): void {
    this.textControlActionableChanges$
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe((newVal: any) => {
        this.value = newVal;

        if (this.jsonIsValid) {
          propagateChanges(this.value); // always propagate parsed object
        }
      });
  }

  registerOnTouched(touchCb: any): void {
    this.textControl.valueChanges
      .pipe(first(), takeUntil(this.onDestroyEmitter))
      .subscribe(touchCb);
  }

  protected onDestroyEmitter = new EventEmitter<void>();
  ngOnDestroy(): void {
    this.onDestroyEmitter.emit();
    this.onDestroyEmitter.complete();
  }
}
